import HighchartsReact from "highcharts-react-official";
import {
    DEFAULT_AXIS_LABEL_PROPS,
    DEFAULT_AXIS_PROPS,
    DEFAULT_COLUMN_PLOT_OPTIONS,
    DEFAULT_TOOLTIP_PROPS,
} from "../../consts";
import { WindowSizeState, useWindowSize } from "~/utils/useWindowSize";
import Highcharts from "highcharts/highstock";
import { formatXAxisTick } from "../functions/formatXAxisTick";
import {
    TimeSeriesPoint,
    TimeSeriesItem,
    TimeSeriesUnits,
    TimeSeriesView,
    ChartTypes,
} from "~/modules/token_timeseries/types";
import ReactDOMServer from "react-dom/server";
import { formatCurrencyValueCompact } from "../../../../utils/functions/formatCurrencyValueCompact";
import { hexToRGB } from "~/utils/hexToRGB";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartLine } from "@fortawesome/pro-regular-svg-icons";
import React from "react";
import { LegendProps, RangeSelectorProps } from "./types";
import {
    DEFAULT_RANGE_SELECTOR_PROPS,
    DEFAULT_TIMESERIES_LEGEND_PROPS,
    NAVIGATOR_STYLE,
} from "./consts";
import {
    ColumnPlotOptions,
    LinePlotOptions,
    MetricsToTimeSeriesViewFunction,
    NavigatorControlState,
    YAxisConfig,
    YAxisTypes,
} from "../Timeseries/types";
import { DEFAULT_NAVIGATOR_CONTROLS } from "../Timeseries/consts";
import { TooltipContent } from "./TooltipContent";
import { calculateTooltipTotal } from "./calculateTooltipTotal";
import { RWA_COLORS } from "~/styles/colors";
import { formatPercentValue } from "~/utils/functions/formatPercentValue";

// // // //

/**
 * Finds the `TimeSeriesView` with the most entries in the `item.points` array.
 * @param {TimeSeriesView[]} series - Array of `TimeSeriesView` objects.
 * @returns {TimeSeriesView | undefined} The `TimeSeriesView` with the most points or `undefined` if the array is empty.
 */
function findLongestSeries(
    series: TimeSeriesView[],
): TimeSeriesView | undefined {
    if (series.length === 0) {
        return undefined;
    }

    let maxView = series[0]; // Start with the first view as the maximum
    let maxPointsLength = maxView.item.points.length; // Length of points in the first view

    for (let i = 1; i < series.length; i++) {
        const currentView = series[i];
        const currentPointsLength = currentView.item.points.length;
        if (currentPointsLength > maxPointsLength) {
            maxView = currentView;
            maxPointsLength = currentPointsLength;
        }
    }

    return maxView;
}

// // // //

// Sort the items so largest-latest-value is at the top of the chart
// Items with group.name starting with "Other (" are always sorted to the bottom
export function sortSeries(metrics: TimeSeriesView[]): TimeSeriesView[] {
    return metrics.sort((a, b) => {
        const aIsOther = a.item.group.name.startsWith("All Others (");
        const bIsOther = b.item.group.name.startsWith("All Others (");

        // If one is "Other" and the other isn't, sort "Other" to the bottom
        if (aIsOther && !bIsOther) return 1;
        if (!aIsOther && bIsOther) return -1;

        // Otherwise, sort by last value descending
        const lastValueA = a.item.points[a.item.points.length - 1][1];
        const lastValueB = b.item.points[b.item.points.length - 1][1];

        return lastValueB - lastValueA;
    });
}

// // // //

// Sort the items so largest-latest-value is at the top of the chart
function sortMetrics(metrics: TimeSeriesItem[]): TimeSeriesItem[] {
    return metrics.sort((a, b) => {
        const lastValueA = a.points[a.points.length - 1][1];
        const lastValueB = b.points[b.points.length - 1][1];

        return lastValueB - lastValueA;
    });
}

// // // //

function getChartOptions(props: {
    metrics: TimeSeriesView[];
    windowSize: WindowSizeState;
    area: boolean;
    stacked: boolean;
    enableZoom: boolean;
    navigator: NavigatorControlState;
    rangeSelector: Partial<RangeSelectorProps>;
    height: number;
    legend: LegendProps;
    showTotal: boolean;
    yAxis: YAxisConfig;
    columnPlotOptions?: ColumnPlotOptions;
    linePlotOptions?: LinePlotOptions;
    lineWidth: number;
    maxTooltipEntries?: number;
    currencyValueDecimals?: number;
}) {
    const {
        metrics,
        windowSize,
        enableZoom = false,
        area,
        stacked,
        navigator,
        rangeSelector,
        height,
        legend,
        showTotal = false,
        yAxis,
        columnPlotOptions,
        linePlotOptions,
        lineWidth,
        maxTooltipEntries,
        currencyValueDecimals,
    } = props;

    // Determine units (default to dollars just to be safe incase metrics[0] is empty)
    const unit: TimeSeriesUnits = metrics[0]
        ? metrics[0].item.measure.unit
        : TimeSeriesUnits.dollar;

    // Hide legend on small screens or when there's only a single series
    let showLegend = legend.enabled || false;
    if (windowSize.isSm || metrics.length <= 1) {
        showLegend = false;
    }
    const shouldTruncateLegendLabels = legend.truncateLabels ?? true;
    const legendLabelStyle = shouldTruncateLegendLabels
        ? "display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;"
        : "display:block;overflow:visible;text-overflow:clip;white-space:nowrap;";

    const xAxisMin = navigator.xAxis ? navigator.xAxis.min : undefined;
    const xAxisMax = navigator.xAxis ? navigator.xAxis.max : undefined;

    const chart: any = {
        height,
        backgroundColor: "transparent",
        animation: false, // Disable animation
        // ENHANCEMENT - force zoom controls off in mobile via windowSize?
        zooming: {
            mouseWheel: {
                enabled: enableZoom,
            },
        },
    };

    if (area) {
        chart.type = "area";
    }

    // Compute the maximum timestamp across all series to clamp the navigator's
    // right edge so it cannot be dragged into empty space past the last data point.
    // Use reduce instead of Math.max(...arr) to avoid "Maximum call stack size exceeded"
    // when there are many points (spreading a huge array exceeds engine argument limits).
    const allTimestamps = metrics.flatMap((m) =>
        m.item.points.map((p) => new Date(p[0]).getTime()),
    );
    const dataMaxTimestamp =
        allTimestamps.length > 0
            ? allTimestamps.reduce((a, b) => Math.max(a, b), -Infinity)
            : undefined;

    // Define navigatorConfig for Highcharts
    const navigatorConfig: any = {
        enabled: navigator.active,
        height: NAVIGATOR_STYLE.height,
        margin: NAVIGATOR_STYLE.margin,
        maskFill: NAVIGATOR_STYLE.maskFill,
        maskInside: NAVIGATOR_STYLE.maskInside,
        outlineColor: NAVIGATOR_STYLE.outlineColor,
        outlineWidth: NAVIGATOR_STYLE.outlineWidth,
        handles: NAVIGATOR_STYLE.handles,
        xAxis: { max: dataMaxTimestamp },
    };

    // If navigator is enabled, select the longest series to function as the basline for the navigator display
    if (navigator.enabled && navigator.active) {
        // Find longest series
        const longestSeries = findLongestSeries(metrics);

        // Use series for navigator display if available
        if (longestSeries) {
            navigatorConfig.series = {
                type: "area",
                color: NAVIGATOR_STYLE.series.color,
                lineWidth: NAVIGATOR_STYLE.series.lineWidth,
                fillColor: {
                    linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
                    stops: NAVIGATOR_STYLE.series.fillColorStops,
                },
                data: longestSeries.item.points.map((v: TimeSeriesPoint) => {
                    return [new Date(v[0]).getTime(), Number(v[1])];
                }),
            };
        }
    }

    const opts = {
        title: null,
        chart,
        tooltip: {
            useHTML: true,
            shared: true, // ENHANCEMENT - add prop to control this?
            // // // //
            // Renders the tooltip outside the chart's SVG
            // https://api.highcharts.com/highcharts/tooltip.outside
            outside: true,
            distance: 20,
            backgroundColor: "var(--card)",
            style: {
                zIndex: 250,
                color: "var(--foreground)",
            },
            // // // //
            ...DEFAULT_TOOLTIP_PROPS,
            formatter: function (this: any) {
                const point = this.point;
                const xAxisDate = new Date(point.category);
                const xAxisLabel = formatXAxisTick(xAxisDate);
                const time = xAxisDate.getTime();

                // Collect name of all series that are enabled
                const selectedSeriesNames = this.points.map(
                    (p: any) => p.series.name,
                );

                // NOTE - isn't very efficient
                const matchedSeries = metrics
                    .filter((m) => {
                        return selectedSeriesNames.includes(m.item.group.name);
                    })
                    .map((m) => {
                        return {
                            ...m,
                            item: {
                                ...m.item,
                                points: m.item.points.filter((p) => {
                                    return new Date(p[0]).getTime() === time;
                                }),
                            },
                        };
                    })
                    .filter((m) => m.item.points.length > 0);

                // ENHANCEMENT - replace this with a more optimized approach (like string interpolation)
                return ReactDOMServer.renderToString(
                    <TooltipContent
                        x={xAxisLabel}
                        data={matchedSeries.map((m) => m.item)}
                        unit={unit}
                        stacked={stacked}
                        total={
                            showTotal
                                ? calculateTooltipTotal(matchedSeries)
                                : undefined
                        }
                        maxTooltipEntries={maxTooltipEntries}
                        currencyValueDecimals={currencyValueDecimals}
                    />,
                );
            },
        },
        yAxis: {
            ...(yAxis.min !== null && yAxis.min !== undefined
                ? { min: yAxis.min }
                : {}),
            // type: "linear" (default) | "logarithmic" | "datetime" | "category"
            type: yAxis.type,

            // // // //
            // crosshair: {
            //     width: 1,
            //     color: "red",
            //     dashStyle: "dash",
            // },
            // // // //

            ...DEFAULT_AXIS_PROPS,
            // ENHANCEMENT - control grid lines via props
            // gridLineWidth: 0, // Set the width of the vertical grid lines
            // lineWidth: 0,
            // tickWidth: 0,
            labels: {
                ...DEFAULT_AXIS_LABEL_PROPS,
                // Show/hide yAxis labels
                enabled: yAxis.enabled !== undefined ? yAxis.enabled : true,
                // TODO - add unit support to this
                formatter: function (): string {
                    // ENHANCEMENT - make this a bit more typesafe, use parseInt
                    // @ts-ignore
                    const value = this.value;

                    // Support stacked percent layout
                    if (stacked) {
                        return `${value}%`;
                    }

                    // Return dollars format
                    if (
                        unit === TimeSeriesUnits.dollar ||
                        unit === TimeSeriesUnits.dollars
                    ) {
                        return `$${formatCurrencyValueCompact(
                            value,
                            currencyValueDecimals,
                        )}`;
                    }

                    // Return percent format
                    if (unit === TimeSeriesUnits.percent) {
                        return `${formatPercentValue(value)}%`;
                    }

                    // Return tokens format
                    if (unit === TimeSeriesUnits.token) {
                        return formatCurrencyValueCompact(value);
                    }

                    // Return tokens format
                    if (unit === TimeSeriesUnits.year) {
                        return `${value} years`;
                    }

                    // Return tokens format
                    if (unit === TimeSeriesUnits.address) {
                        return `${value.toLocaleString()}`;
                    }

                    // Ensure numeric before invoking toLocaleString
                    if (typeof value === "number") {
                        return value.toLocaleString();
                    }

                    return value;
                },
            },
        },
        xAxis: {
            type: "datetime",
            ...DEFAULT_AXIS_PROPS,
            // ENHANCEMENT - control grid lines via props
            // gridLineWidth: 0, // Set the width of the vertical grid lines
            // lineWidth: 0,
            // tickWidth: 0,
            min: xAxisMin,
            max: xAxisMax ? xAxisMax : dataMaxTimestamp,
            crosshair: {
                width: 1,
                color: RWA_COLORS.slate["300"],
                dashStyle: "Dash",
            },
            labels: {
                ...DEFAULT_AXIS_LABEL_PROPS,
                // ENHANCEMENT - control x-axis visibility via props
                formatter: function (): string {
                    // @ts-ignore
                    const value = this.value;

                    return formatXAxisTick(new Date(value));
                },
            },
        },
        plotOptions: {
            series: {
                type: "spline",
                animation: {
                    duration: 0,
                },
                events: {
                    // TODO - allow this to be controlled via props
                    // TODO - add analytics for this
                    legendItemClick: function () {
                        return true;
                    },
                },
            },
        },
        accessibility: {
            enabled: false,
        },
        credits: {
            enabled: false,
        },
        legend: {
            ...legend,
            enabled: showLegend,
            useHTML: true,
            itemStyle: {
                color: "var(--foreground)",
                ...(shouldTruncateLegendLabels
                    ? {}
                    : {
                          textOverflow: "clip",
                          whiteSpace: "nowrap",
                      }),
            },
            // Label formatter includes circular legend symbols
            labelFormatter: function (this: any) {
                return `<div class="flex items-center gap-2">
                    <div class="w-3 min-w-3 h-3 rounded-full" style="background-color: ${this.color};"></div>
                    <span style="${legendLabelStyle}">${this.name}</span>
                </div>`;
            },
            // Hide default legend symbols
            squareSymbol: false,
            symbolRadius: 0,
            symbolWidth: 0,
            symbolHeight: 0,
        },
        navigator: navigatorConfig,
        rangeSelector: {
            ...DEFAULT_RANGE_SELECTOR_PROPS,
            labelStyle: {
                display: "none",
            },
            buttonTheme: {
                // styles for the buttons
                fill: "none",
                stroke: "none",
                "stroke-width": 0,
                r: 8,
                style: {
                    color: RWA_COLORS["blue"]["800"],
                    fontWeight: "bold",
                },
                states: {
                    hover: {},
                    select: {
                        fill: RWA_COLORS["blue"]["800"],
                        style: {
                            color: "white",
                        },
                    },
                },
            },
            ...rangeSelector,
            // buttons: [
            //     {
            //         count: 1,
            //         type: "month",
            //         text: "1M",
            //     },
            //     {
            //         count: 3,
            //         type: "month",
            //         text: "3M",
            //     },
            //     {
            //         type: "all",
            //         text: "All",
            //     },
            // ],
        },
        series: [
            ...metrics
                .filter((m) => m.enabled)
                .map((m) => {
                    const color = m.item.group.color || "#999999";

                    return {
                        name: `${m.item.group.name}`,
                        tooltip: {
                            valueDecimals: currencyValueDecimals ?? 2,
                        },
                        type: m.chartType,
                        lineWidth: m.lineWidth ?? lineWidth,
                        opacity: m.opacity,
                        showInLegend: m.showInLegend,
                        zIndex: m.zIndex,
                        color:
                            m.chartType === ChartTypes.column
                                ? hexToRGB(color, 0.6)
                                : color,
                        data: m.item.points.map((v: TimeSeriesPoint) => {
                            if (m.invertYAxis) {
                                return [
                                    new Date(v[0]).getTime(),
                                    Number(v[1]) * -1,
                                ];
                            }
                            return [new Date(v[0]).getTime(), Number(v[1])];
                        }),
                        // // // //
                        // ENHANCEMENT - expose data grouping control via props
                        // dataGrouping: {
                        //     approximation: "sum",
                        //     enabled: true,
                        //     forced: true, // Force this always (default behavior will change grouping based on chart size)
                        //     units: [["day", [1]]],
                        //     // units: [["week", [1]]],
                        //     // units: [["month", [1]]],
                        //     // units: [["year", [1]]],
                        // },
                        // // // //
                        fillColor: area
                            ? {
                                  linearGradient: {
                                      x1: 0,
                                      y1: 0,
                                      x2: 0,
                                      y2: 1,
                                  },
                                  stops: [
                                      [0, hexToRGB(color, 0.8)], // Use the first color in the Highcharts color palette
                                      [1, hexToRGB(color, 0.2)], // Use a transparent color for the end of the fill
                                  ],
                              }
                            : undefined,
                    };
                }),
        ],
    };

    // // // //

    if (area) {
        // @ts-ignore
        opts.plotOptions.area = {
            stacking: stacked ? "percent" : "overlap", // "percent" | "normal" | "percent" | "overlap"
            marker: {
                enabled: false, // Disable markers on the data points
            },
            states: {
                inactive: {
                    opacity: 1,
                },
            },
        };
    }

    // // // //
    // Handle props.columnPlotOptions
    if (
        columnPlotOptions ||
        metrics.some((m) => m.chartType === ChartTypes.column)
    ) {
        let columnPlotOptionsValue: ColumnPlotOptions = {
            ...DEFAULT_COLUMN_PLOT_OPTIONS,
        };

        if (columnPlotOptions) {
            columnPlotOptionsValue = {
                ...columnPlotOptionsValue,
                ...columnPlotOptions,
            };
        }

        // @ts-ignore
        opts.plotOptions.column = columnPlotOptionsValue;
    }

    // // // //
    // Handle props.linePlotOptions
    if (linePlotOptions) {
        // @ts-ignore
        opts.plotOptions.line = linePlotOptions;
        // @ts-ignore
        opts.plotOptions.spline = linePlotOptions;
    }

    // // // //

    return opts;
}

// // // //
// Define interfaces for component props

interface MetricsProps {
    metrics: TimeSeriesItem[];
    series?: never;
}

interface SeriesProps {
    metrics?: never;
    series: TimeSeriesView[];
}

type DataProps = MetricsProps | SeriesProps;

interface Props {
    area?: boolean;
    stacked?: boolean;
    legend?: LegendProps;
    enableZoom?: boolean;
    showTotal?: boolean;
    height?: number;
    navigator?: NavigatorControlState;
    rangeSelector?: Partial<RangeSelectorProps>;
    yAxis?: YAxisConfig;
    columnPlotOptions?: ColumnPlotOptions;
    linePlotOptions?: LinePlotOptions;
    lineWidth?: number;
    maxTooltipEntries?: number;
    currencyValueDecimals?: number;
    metricsToTimeSeriesView?: MetricsToTimeSeriesViewFunction;
}

/**
 * TimeSeriesChart
 * Renders chart for historical metrics
 * ENHANCEMENT - simplify the props here (perhaps it should accept VisualizationMode directly?)
 */
export function TimeSeriesChartLayout(props: Props & DataProps) {
    const {
        metrics,
        series,
        area = false,
        stacked = false,
        enableZoom = false,
        navigator = DEFAULT_NAVIGATOR_CONTROLS,
        showTotal = false,
        legend = { ...DEFAULT_TIMESERIES_LEGEND_PROPS },
        rangeSelector = DEFAULT_RANGE_SELECTOR_PROPS,
        height = 300,
        columnPlotOptions,
        linePlotOptions,
        lineWidth = 1,
        maxTooltipEntries = 25,
        currencyValueDecimals,
        metricsToTimeSeriesView = null,
        yAxis = {
            min: 0,
            enabled: true,
            type: YAxisTypes.linear,
        },
    } = props;

    // // // //
    // Handle both TimeSeriesView[] + TimeSeriesItem[] props
    let chartSeries: TimeSeriesView[] = [];
    if (series) {
        chartSeries = sortSeries(series);
    } else if (metricsToTimeSeriesView !== null) {
        chartSeries = metricsToTimeSeriesView(sortMetrics(metrics));
    } else {
        chartSeries = sortSeries(
            metrics.map((m) => {
                return {
                    item: m,
                    enabled: true,
                    chartType: ChartTypes.area,
                };
            }),
        );
    }
    // // // //

    const windowSize = useWindowSize();

    // TODO - annotate this, make sure its correct
    if (
        chartSeries.length === 0 ||
        (chartSeries.length === 1 && chartSeries[0].item.points.length < 2)
    ) {
        return (
            <div
                style={{ height: `${height}px` }}
                className="flex flex-col items-center justify-center gap-3 text-muted-foreground"
            >
                <FontAwesomeIcon icon={faChartLine} className="h-8 w-8" />
                <p className="text-sm font-light">
                    No data available for this selection
                </p>
            </div>
        );
    }

    const options = getChartOptions({
        metrics: chartSeries,
        windowSize,
        area,
        stacked,
        enableZoom,
        navigator,
        height,
        showTotal,
        legend,
        columnPlotOptions,
        linePlotOptions,
        lineWidth,
        yAxis,
        maxTooltipEntries,
        currencyValueDecimals,
        rangeSelector,
    });

    return <HighchartsReact highcharts={Highcharts} options={options} />;
}

export const TimeSeriesChart = React.memo(TimeSeriesChartLayout);
